/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from "@emotion/react";
import { Box } from "@material-ui/core";
import { CommentItemPurchasedHiddenName } from "components/streaming/commentItem/CommentItemPurchasedHiddenName";
import { useI18n } from "hooks/i18n/i18n";
import React from "react";

import { useSelector } from "../../store";
import { CommentItemNormalV2 } from "./CommentItemNormalV2";
import { CommentItemSuperChatV2 } from "./CommentItemSuperChatV2";

import type { Store } from "../../store";
import type { DisplayComment } from "./applyBlockedComments";
import type { ProductData, UserMessage } from "@spwn/types/firebase/firestore";

type Props = {
  message: DisplayComment;
  isCommentUser: boolean;
  isChatAdmin: boolean;
  isDarkModeEnabled: boolean;
  eventGoodsMap: { [key: string]: ProductData };
  giftAmountList: number[];
  isMenuOpen: boolean;
  onOpenMenu: () => void;
  onCloseMenu: () => void;
  onClickBanUser: (comment: Pick<UserMessage, "_id" | "uid">) => Promise<void>;
  onClickBanComment: (idx: string) => Promise<void>;
  onClickBlockUser:
    | ((comment: Pick<UserMessage, "uid" | "name">) => Promise<void>)
    | undefined;
  msgDeleteFunction: (idx: string) => void;
};

export const StreamingCommentItemV2: React.FC<Props> = (props) => {
  const giftItemMap = useSelector(
    (state: Store) => state.streaming.giftItemMap
  );
  const { t } = useI18n();

  const { isSuperChat, isPurchased, purchasedProductId } = props.message;
  const purchasedProduct: ProductData = isPurchased
    ? // @ts-expect-error TS2538
      props.eventGoodsMap?.[purchasedProductId]
    : null;

  // ギフトと商品購入は同時に行われないので、両方がtrueになることはない
  // 正しくない値のコメントなので、nullを返す
  if (isSuperChat && isPurchased) {
    return null;
  }

  // 自分以外のコメントがBANされた場合は削除されました表示にする
  if (!props.isCommentUser && props.message.isBan) {
    // ただし、NGワードなコメントや他のユーザーが自分で自分のコメントを削除した場合は単に非表示にする
    if (["delete", "ngWord"].includes(props.message.banReason ?? "")) {
      return null;
    }

    return (
      // 通常コメントの行高は 24px のユーザーアイコンで決まる。行高を同じ 24px にして
      // padding と合わせ 32px に揃えないと、墓標に変わった行だけ縮んで以降のコメントが
      // 縦にずれる。box-sizing: border-box が全体に効いているため、min-height ではなく
      // 行高で決める（min-height だと枠込みの値になり内容が縮む）。
      <Box
        padding={"4px 12px"}
        color={"gray"}
        fontSize={"12px"}
        lineHeight={"24px"}
      >
        {props.message.isPersonallyBlocked
          ? t("streaming.comment.blocked")
          : t("streaming.comment.deleted")}
      </Box>
    );
  }

  // スパチャコメントの時の表示
  if (isSuperChat && !isPurchased) {
    const { giftItemId } = props.message;
    const giftItem = giftItemId ? giftItemMap?.[giftItemId] : undefined;

    return (
      <CommentItemSuperChatV2
        userIcon={props.message.icon}
        userName={props.message.name}
        isCommentUser={props.isCommentUser}
        isChatAdmin={props.isChatAdmin}
        amount={props.message.amount}
        message={props.message}
        backgroundColor={props.message.color}
        options={{
          color: giftItem?.textColor ?? "#fff",
          stickerImageUrl: giftItem && giftItem?.imgUrl,
          backgroundImageUrl: giftItem && giftItem?.bgImgUrl,
        }}
        isMenuOpen={props.isMenuOpen}
        onOpenMenu={props.onOpenMenu}
        onCloseMenu={props.onCloseMenu}
        onClickBanUser={props.onClickBanUser}
        onClickBanComment={props.onClickBanComment}
        onClickBlockUser={props.onClickBlockUser}
      />
    );
  }

  // 商品を購入した時の表示
  if (!isSuperChat && isPurchased) {
    return (
      <CommentItemPurchasedHiddenName purchasedProduct={purchasedProduct} />
    );
  }

  // isSuperChatとisPurchasedがfalseの時、通常のコメントの表示する
  return (
    <CommentItemNormalV2
      message={props.message}
      isCommentUser={props.isCommentUser}
      isChatAdmin={props.isChatAdmin}
      isDarkModeEnabled={props.isDarkModeEnabled}
      isMenuOpen={props.isMenuOpen}
      onOpenMenu={props.onOpenMenu}
      onCloseMenu={props.onCloseMenu}
      onClickBanUser={props.onClickBanUser}
      onClickBanComment={props.onClickBanComment}
      onClickBlockUser={props.onClickBlockUser}
      msgDeleteFunction={props.msgDeleteFunction}
    />
  );
};
